import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowRight, Copy, Check, Link2, MapPin, ChevronDown, Facebook, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={props.size || 15} height={props.size || 15} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={props.size || 15} height={props.size || 15} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const CITIES = ['Toronto', 'Vancouver', 'Montréal', 'Calgary', 'Ottawa', 'Edmonton', 'Winnipeg', 'Other'];

const WaitlistForm = ({ refCode, onJoined }) => {
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const inviteLink = result
    ? `${window.location.origin}/?ref=${result.referral_code}`
    : '';

  const shareText = encodeURIComponent(
    'I just joined the DineOra waitlist — order direct from local independent restaurants, no middleman. Skip the line with my invite link:'
  );
  const shareUrl = encodeURIComponent(inviteLink);
  const SHARES = [
    { name: 'X', href: `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`, Icon: XIcon, testId: 'share-link-x' },
    { name: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`, Icon: Facebook, testId: 'share-link-facebook' },
    { name: 'WhatsApp', href: `https://wa.me/?text=${shareText}%20${shareUrl}`, Icon: WhatsAppIcon, testId: 'share-link-whatsapp' },
    { name: 'Text message', href: `sms:?&body=${shareText}%20${shareUrl}`, Icon: MessageSquare, testId: 'share-link-sms' },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ref: refCode || null, city: city || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Something went wrong.');
      setResult(data);
      onJoined?.(data);
      toast.success(
        data.already_joined ? 'You were already on the list.' : "You're on the list."
      );
    } catch (err) {
      toast.error(err.message || 'Could not join right now. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success('Invite link copied.');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — select the link manually.');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <AnimatePresence mode="wait">
        {!result ? (
          <motion.form
            key="form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-3"
            data-testid="waitlist-form"
          >
            <div className="flex items-center gap-3 bg-white border border-black/10 rounded-full px-5 py-4 transition-colors duration-300 focus-within:border-[#f15a24]/60">
              <Mail size={18} className="text-[#97917f] shrink-0" aria-hidden="true" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="hero-email-input"
                className="bg-transparent flex-1 min-w-0 text-[#171512] placeholder-[#97917f] text-base outline-none"
              />
            </div>
            <div className="relative">
              <MapPin size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#97917f] pointer-events-none" aria-hidden="true" />
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                data-testid="hero-city-select"
                className={`w-full appearance-none bg-white border border-black/10 rounded-full pl-11 pr-10 py-4 text-base outline-none transition-colors duration-300 focus:border-[#f15a24]/60 ${city ? 'text-[#171512]' : 'text-[#97917f]'}`}
              >
                <option value="">Your city (optional)</option>
                {CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-5 top-1/2 -translate-y-1/2 text-[#97917f] pointer-events-none" aria-hidden="true" />
            </div>
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              data-testid="hero-email-submit-btn"
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[#f15a24] hover:bg-[#d94a1a] text-white font-semibold text-base py-4 transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? 'Saving your spot…' : 'Notify me'}
              {!loading && <ArrowRight size={18} aria-hidden="true" />}
            </motion.button>
          </motion.form>
        ) : (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white border border-black/10 shadow-sm rounded-3xl p-8 text-center"
            data-testid="hero-referral-card"
          >
            <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#f15a24]">
              {result.already_joined ? 'Already on the list' : "You're in"}
            </p>
            <p className="mt-4 font-display text-5xl sm:text-6xl text-[#171512]">
              <span className="text-[#97917f] text-3xl align-middle">#</span>
              <span data-testid="hero-position-in-line">{result.position}</span>
            </p>
            <p className="mt-1 text-[#6e6a63] text-sm">in line for the door</p>

            <div className="mt-7 text-left">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#6e6a63] mb-2 flex items-center gap-2">
                <Link2 size={12} aria-hidden="true" /> Your personal invite link
              </p>
              <div className="flex items-center gap-2 bg-[#f4f2ed] border border-black/10 rounded-full pl-4 pr-1.5 py-1.5">
                <input
                  readOnly
                  value={inviteLink}
                  data-testid="invite-link-input"
                  className="bg-transparent flex-1 min-w-0 text-xs text-[#6e6a63] outline-none truncate"
                />
                <button
                  type="button"
                  onClick={copyLink}
                  data-testid="hero-referral-copy-btn"
                  className="shrink-0 flex items-center gap-1.5 rounded-full bg-[#f15a24] hover:bg-[#d94a1a] text-white text-xs font-semibold px-4 py-2 transition-colors duration-300"
                >
                  {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="mt-3 text-xs text-[#97917f] leading-relaxed" data-testid="waitlist-position-badge">
                {result.referrals > 0
                  ? `${result.referrals} friend${result.referrals === 1 ? '' : 's'} joined through you — that moved you up the line.`
                  : 'Every 5 friends who join through your link moves you 5 places up the line.'}
              </p>

              <p className="mt-5 mb-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#6e6a63]">
                Share your link
              </p>
              <div className="flex gap-2">
                {SHARES.map(({ name, href, Icon, testId }) => (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Share on ${name}`}
                    data-testid={testId}
                    className="w-10 h-10 rounded-full border border-black/10 bg-white flex items-center justify-center text-[#6e6a63] hover:text-[#f15a24] hover:border-[#f15a24]/50 transition-colors duration-300"
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WaitlistForm;
